import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle } from "lucide-react";
import { SITE, WA_DEFAULT } from "@/lib/site";

const LogoMark = () => (
  <svg viewBox="0 0 64 64" className="h-9 w-9" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#171F26" />
    <circle cx="32" cy="32" r="17" fill="none" stroke="#D4AF37" strokeWidth="3" />
    <circle cx="32" cy="32" r="4" fill="#D4AF37" />
    <path d="M32 15v34M15 32h34M20 20l24 24M44 20L20 44" stroke="#D4AF37" strokeWidth="2" />
  </svg>
);

const LANGS = ["EN", "OD", "HI"];

export default function Navbar({ lang, setLang, onBook, absoluteLinks }) {
  const [open, setOpen] = useState(false);
  const base = absoluteLinks ? "/" : "";
  const links = [
    { label: "Services", href: `${base}#services` },
    { label: "Packages", href: `${base}#packages` },
    { label: "Fleet", href: `${base}#fleet` },
    { label: "Estimate", href: `${base}#estimator` },
    { label: "Contact", href: `${base}#contact` },
  ];
  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0F1418]/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
        <a href="/" data-testid="nav-logo" className="flex items-center gap-3">
          <LogoMark />
          <span className="font-serif text-lg font-semibold tracking-tight">
            Jay Jagannath <span className="text-[#D4AF37]">Travels</span>
          </span>
        </a>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-white/70 transition-colors hover:text-[#D4AF37]"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <div data-testid="nav-lang-switcher" className="flex overflow-hidden rounded-full border border-white/15 text-xs">
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`nav-lang-${l.toLowerCase()}`}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1.5 transition-colors ${lang === l ? "bg-[#D4AF37] font-semibold text-[#0F1418]" : "text-white/60 hover:text-white"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href={SITE.phoneHref}
            data-testid="nav-phone-btn"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
            aria-label="Call us"
          >
            <Phone className="h-4 w-4" />
          </a>
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-btn"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
            aria-label="WhatsApp us"
          >
            <MessageCircle className="h-4 w-4" />
          </a>
          <button
            data-testid="nav-book-btn"
            onClick={onBook}
            className="rounded-full bg-[#D4AF37] px-5 py-2 text-sm font-semibold text-[#0F1418] transition-transform hover:scale-105"
          >
            Book Your Trip
          </button>
        </div>
        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className="flex h-9 w-9 flex-col items-center justify-center gap-1.5 lg:hidden"
          aria-label="Menu"
        >
          <span className={`h-px w-6 bg-white transition-transform ${open ? "translate-y-1 rotate-45" : ""}`} />
          <span className={`h-px w-6 bg-white transition-transform ${open ? "-translate-y-0.5 -rotate-45" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="border-t border-white/10 bg-[#0F1418]/95 px-4 py-4 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-2.5 text-sm text-white/80"
            >
              {l.label}
            </a>
          ))}
          <button
            data-testid="nav-mobile-book-btn"
            onClick={() => { setOpen(false); onBook(); }}
            className="mt-3 block w-full rounded-full bg-[#D4AF37] px-5 py-2.5 text-center text-sm font-semibold text-[#0F1418]"
          >
            Book Your Trip
          </button>
        </div>
      )}
    </motion.header>
  );
}
